/* @HEADER
 * @version 1.1.298 | 2026-10-18
 * @purpose Tells the diagnostic recorder's seam once for every change of the space on screen (ZVX-DEV-7676): its kind and how the person got there — the one place that watches the change itself, so no caller can forget to say it or say it twice.
 * @sidecar useBoardChangeTell.ts.header.md
 * @layer app | @hex adapter | @ctx navigation
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-7676, ZVX-DEV-6430

import { useEffect, useRef } from 'react'
import { emitUiAction } from '@zvenix/tasks'
// @ts-expect-error — framework-free .mjs decisions, typed by their JSDoc
import { boardKindOf } from './boardChangeDecisions.mjs'

/** How the person reached the space: the closed list the recorder's registry accepts. */
export type NavVia = 'click' | 'search' | 'hotkey' | 'back' | 'other'

export interface UseBoardChangeTellArgs {
  activeSpaceId: string | null
  boardGroups: ReadonlyArray<{ id: string; system_kind?: string }>
  /** The way recorded for the move that just happened; `other` when nobody said. */
  takeNavVia: () => NavVia
}

/** Entering a space is told; leaving to the spaces list is not. The effect runs when the id changes, so staying put is not told either. */
export function useBoardChangeTell({ activeSpaceId, boardGroups, takeNavVia }: UseBoardChangeTellArgs): void {
  const groupsRef = useRef(boardGroups)
  groupsRef.current = boardGroups
  useEffect(() => {
    if (!activeSpaceId) return
    const group = groupsRef.current.find((g) => g.id === activeSpaceId) ?? { id: activeSpaceId }
    emitUiAction({ type: 'nav.board', boardKind: boardKindOf(group), via: takeNavVia() })
  }, [activeSpaceId, takeNavVia])
}
